// src/components/appBar.tsx

import { useAuth0 } from "@auth0/auth0-react";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Switch,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useAppId } from "../hook";
import { useToggleAiPanel } from "../hook/ai-panel";
import {
  useNavigateSettingsPage,
  useSubscriptionStatus,
} from "../hook/settings";
import { ToggleSideNavButton } from "./sideNav";
import { useIsDarkMode } from "../hook/setting";
import { useSideNavOpen } from "../hook/sideNav";
import { EditorToolbar } from "./page/block/EditorToolbar";
import { CasLogo } from "./brand/CasLogo";
import { useIsPhone, usePhoneView } from "../context/phoneView";
import { resetAgentProvider } from "../hook/agentProvider";

export const AppBarSection: React.FC<{ drawerWidth: string }> = ({
  drawerWidth,
}) => {
  const { sideNavOpen } = useSideNavOpen();
  const [appId] = useAppId();
  const { loginWithPopup, user, logout, isAuthenticated } = useAuth0();
  const navigateSettingsPage = useNavigateSettingsPage();
  const toggleAiPanel = useToggleAiPanel();
  const isPhone = useIsPhone();
  const { setView } = usePhoneView();
  const email = user?.email;
  const { hasAccess } = useSubscriptionStatus();

  // Dropdown menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    // The module keeps the previous account's provider row; a different sign-in must read fresh.
    resetAgentProvider();
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleSettings = () => {
    handleMenuClose();
    navigateSettingsPage();
  };
  const theme = useTheme();
  const { isDarkMode, setDarkMode } = useIsDarkMode();

  return (
    <AppBar
      sx={{
        transition: (theme) =>
          theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        width: !isPhone && sideNavOpen ? `calc(100% - ${drawerWidth})` : "100%",
        marginLeft: !isPhone && sideNavOpen ? `${drawerWidth}` : 0,
        backgroundColor: (theme) =>
          isDarkMode
            ? theme.palette.background.paper
            : theme.palette.secondary.main,
      }}
    >
      <Toolbar>
        {!isPhone && appId && !sideNavOpen && hasAccess && <ToggleSideNavButton />}
        <Link href={`/`}>
          <IconButton edge="start" color="inherit" aria-label="app icon">
            <img
              src={`/omega_logo.png`}
              alt="app icon"
              style={{ height: 24 }}
            />
          </IconButton>
        </Link>
        <Box sx={{ flexGrow: 1 }} />
        {!isAuthenticated && (
          <Button
            color="primary"
            startIcon={<LoginIcon />}
            onClick={() => {
              resetAgentProvider();
              loginWithPopup();
            }}
          >
            Login
          </Button>
        )}
        {isAuthenticated && (
          <>
            <IconButton
              color="primary"
              onClick={isPhone ? () => setView("cas") : toggleAiPanel}
              aria-label="toggle ai panel"
              data-testid="ai-panel-toggle"
              size="large"
            >
              <CasLogo size={28} />
            </IconButton>
            <IconButton
              color="primary"
              onClick={handleMenuOpen}
              aria-label="account menu"
              size="large"
              // ref={(el) => setAnchorEl(el)}
            >
              <AccountCircleIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                disableRipple
                disableTouchRipple
                sx={{
                  pointerEvents: "none",
                  cursor: "default",
                  userSelect: "text",
                  "&:hover": { backgroundColor: "inherit" },
                  py: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="span"
                  sx={{ pr: 0.5 }}
                >
                  Signed in as:{" "}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  sx={{ fontWeight: 500 }}
                >
                  {email}
                </Typography>
              </MenuItem>
              <Divider />
              {appId && (
                <Link
                  href={`/settings`}
                  onClick={() => {
                    handleMenuClose();
                  }}
                >
                  <MenuItem>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    Settings
                  </MenuItem>
                </Link>
              )}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
              <Box
                sx={{
                  padding: "0 0.5rem 0 0.5rem",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Switch
                  checked={isDarkMode}
                  onChange={(e) => setDarkMode(!isDarkMode)}
                />
                <Typography>Dark Mode</Typography>
              </Box>
            </Menu>
          </>
        )}
      </Toolbar>
      <EditorToolbar />
    </AppBar>
  );
};
